import { post } from '../api/client';
import { getSocketURL } from './platform';
import type {
  AppNotification,
  SocketSyncDonePayload,
  SocketSyncInfo,
  SocketTicketResult,
} from '../types';

/**
 * 上行帧名。与后端 `notification-contract.ts` 的 SOCKET_UP_EVENT 逐字对齐——
 * platform-ws 按 `message.event` 查表分发，未注册的名字会被静默丢弃、不报错，
 * 所以这几串字符改任何一端都必须同步改另一端。
 */
const UP = { AUTH: 'auth', PONG: 'pong', SYNC: 'sync' } as const;

/** 下行帧名，对齐后端 SOCKET_DOWN_EVENT */
const DOWN = {
  READY: 'ready',
  NOTIFICATION: 'notification',
  UNREAD: 'unread',
  SYNC_DONE: 'syncDone',
  PING: 'ping',
  ERROR: 'error',
} as const;

/** 关闭码，对齐后端 SOCKET_CLOSE_CODE */
const CLOSE = { UNAUTHORIZED: 4401, CONFLICT: 4408, DEAD: 4410, TOO_MANY: 4429 } as const;

export type SocketStatus = 'idle' | 'connecting' | 'online' | 'offline';

export interface NotificationSocketHandlers {
  onReady?(info: SocketSyncInfo): void;
  onNotification?(item: AppNotification): void;
  onUnread?(count: number): void;
  onSyncDone?(payload: SocketSyncDonePayload): void;
  onError?(message: string): void;
  onStatus?(status: SocketStatus): void;
}

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

/**
 * 通知实时通道的浏览器端单例。
 *
 * 帧信封固定 `{ event, data }`；心跳方向是服务端下行 `ping`、本端回 `pong`，
 * 连续两个周期不回话服务端会以 4410 踢掉本端。
 *
 * 重连策略按关闭码分流：4401（票据过期/已被消费）重新取票即可自愈；
 * 4408 是「同用户连接数超限、本端被淘汰」，立刻重连会反过来踢掉别的标签页，
 * 于是停住不再自动重试，等页面重新可见时由 `resume()` 再试；
 * 4429（服务端过载/未鉴权连接超限）额外多退避两轮。
 *
 * 游标 `cursor` 记录本端已见的最大通知 id：重连后的首个 `ready` 若显示服务端有更新的通知，
 * 就发一次 `sync{sinceId}` 补投断连期间的增量。
 */
export class NotificationSocket {
  private status: SocketStatus = 'idle';
  private socket: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private attempt = 0;
  private cursor = 0;
  private baselined = false;
  private evicted = false;
  private readonly handlers = new Set<NotificationSocketHandlers>();

  /**
   * 注册消费者。连接生命周期完全由消费者计数驱动：
   * 首个消费者加入时建连，最后一个退出时断开——外部无需（也不该）手动开关，
   * 否则一个组件卸载就会掐断另一个组件仍在用的连接。
   */
  subscribe(handlers: NotificationSocketHandlers): () => void {
    this.handlers.add(handlers);
    if (this.handlers.size === 1) this.start();
    return () => {
      this.handlers.delete(handlers);
      if (this.handlers.size === 0) this.stop();
    };
  }

  private start(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.evicted = false;
    this.attempt = 0;
    void this.connect();
  }

  /** 登出/最后一个消费者退出：游标与基线一并归零，避免把上一个账号的进度带给下一个。 */
  private stop(): void {
    this.evicted = false;
    this.attempt = 0;
    this.cursor = 0;
    this.baselined = false;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const socket = this.socket;
    this.socket = null;
    socket?.close();
    this.setStatus('idle');
  }

  /** 网络恢复或页面重新可见时的即时重试（绕过退避等待）。 */
  resume(): void {
    if (this.status === 'online' || this.status === 'connecting') return;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.attempt = 0;
    void this.connect();
  }

  /** 主动拉取增量；同时充当存活探针——服务端一定回 syncDone 或 error。 */
  sync(): void {
    if (!this.baselined) return;
    this.send({ event: UP.SYNC, data: { sinceId: this.cursor } });
  }

  private async connect(): Promise<void> {
    if (this.status !== 'idle' || this.evicted) return;
    this.setStatus('connecting');

    let ticket: SocketTicketResult;
    try {
      // 票据经 Authorization 头换取，令牌不进 URL
      ticket = await post<SocketTicketResult>('/notifications/socket-ticket');
    } catch {
      this.scheduleReconnect();
      return;
    }

    let socket: WebSocket;
    try {
      socket = new WebSocket(getSocketURL(ticket.path));
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.send({ event: UP.AUTH, data: { ticket: ticket.ticket } });
    };
    socket.onmessage = event => this.handleFrame(event.data);
    socket.onclose = event => this.handleClose(event.code);
    // 出错后浏览器必定紧接着触发 onclose，这里不重复处理，否则会双倍重连
    socket.onerror = () => undefined;
  }

  private handleClose(code: number): void {
    this.socket = null;
    if (code === CLOSE.CONFLICT) {
      // 本端是被挤掉的第 9+ 个连接，退让而不是互踢
      this.evicted = true;
      this.setStatus('offline');
      return;
    }
    if (code === CLOSE.UNAUTHORIZED) this.baselined = false;
    if (code === CLOSE.TOO_MANY) this.attempt += 2;
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    this.setStatus('offline');
    const ceiling = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.attempt);
    this.attempt += 1;
    // 抖动：避免后端重启后整批客户端在同一毫秒回头冲垮它
    const delay = Math.round(ceiling * (0.5 + Math.random() * 0.5));
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  private handleFrame(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let frame: { event?: unknown; data?: unknown };
    try {
      frame = JSON.parse(raw) as typeof frame;
    } catch {
      return;
    }
    if (typeof frame.event !== 'string') return;

    switch (frame.event) {
      case DOWN.PING:
        this.send({ event: UP.PONG, data: {} });
        return;
      case DOWN.READY:
        this.handleReady(frame.data as SocketSyncInfo);
        return;
      case DOWN.NOTIFICATION: {
        const item = frame.data as AppNotification;
        this.cursor = Math.max(this.cursor, item.id);
        this.emit(handler => handler.onNotification?.(item));
        return;
      }
      case DOWN.UNREAD: {
        const count = (frame.data as { count: number }).count;
        this.emit(handler => handler.onUnread?.(count));
        return;
      }
      case DOWN.SYNC_DONE: {
        const payload = frame.data as SocketSyncDonePayload;
        this.cursor = Math.max(this.cursor, payload.newestId);
        this.emit(handler => handler.onSyncDone?.(payload));
        return;
      }
      case DOWN.ERROR: {
        const message = (frame.data as { message?: string }).message ?? '实时通道返回错误';
        this.emit(handler => handler.onError?.(message));
        return;
      }
      default:
        if (import.meta.env.DEV) {
          console.warn(`[notificationSocket] 未识别的下行帧 event=${frame.event}`);
        }
    }
  }

  private handleReady(info: SocketSyncInfo): void {
    const since = this.cursor;
    this.cursor = Math.max(this.cursor, info.newestId);
    const needCatchUp = this.baselined && info.newestId > since;
    this.baselined = true;
    this.attempt = 0;
    this.setStatus('online');
    this.emit(handler => handler.onReady?.(info));
    if (needCatchUp) this.send({ event: UP.SYNC, data: { sinceId: since } });
  }

  private send(frame: { event: string; data: unknown }): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(frame));
  }

  private emit(apply: (handler: NotificationSocketHandlers) => void): void {
    for (const handler of this.handlers) apply(handler);
  }

  private setStatus(status: SocketStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.emit(handler => handler.onStatus?.(status));
  }
}

/** 页面级单例：多标签页各自一条连接，同一页面内多个消费者共用一条。 */
export const notificationSocket = new NotificationSocket();
